import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CardRow from '../QuotationHeaderCardRow';
import { Grid, Box, Divider, Hidden, Tooltip, Slide, Chip } from '@mui/material';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import UpdateIcon from '@mui/icons-material/Update';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorIcon from '@mui/icons-material/Error';
import Replay from '@mui/icons-material/Replay';
import { Autocomplete, TextField, Button, Typography, IconButton } from '@totalsoft/rocket-ui';
import CoefficientsComponent from '../CoefficientsComponent';
import RisksComponent from './RisksComponent';
import { get, sequence } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { emptyString, emptyArray, QuotationsStatus } from 'constants/defaultValues';
import { valueOrDefault } from 'utils/functions';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { useReactiveVar } from '@apollo/client';
import { quotationsStatusVar } from 'features/insuranceDocument/variables';
import LoadingWrapper from 'components/common/LoadingWrapper';
import { ConventionNameWrapper, QuotationCard, QuotationButton } from '../CascoStyles';

const QuotationListComponent = ({
  quotationLens,
  validation,
  index,
  colorTheme,
  onSave,
  onRetry,
  onSelect,
  onQuotationLockChange,
  isDirty,
  saving,
  lockUpdating,
  disabled,
  allowEditForNegotiation,
  allowSelection,
  allowCustomQuotationEdit,
  allowQuotationsLockUpdate,
  allowRetry,
  onRiskSelectionChange,
  onCoefficientsSelectionChange,
  onHeaderPropertyChange,
  onAmountChange,
  quotationSelected,
  titleRef,
  showTitleTooltip,
  messageRef,
  showMessageTooltip,
  onEditCustomQuotation,
  allowEmptySelection,
  isLastQuotationModified
}) => {
  const { t } = useTranslation();
  const status = useReactiveVar(quotationsStatusVar);

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const composeId = code => `Quotation_${code}_${index}`;

  const displayLockArea = allowQuotationsLockUpdate || (quotationLens.lockedYN |> get);

  const cardIconColor = useMemo(() => {
    if (quotationLens.sourceQuotation |> get) return 'info';
    if (quotationLens.sourceQuotationId |> get) return 'success';
    return 'primary';
  }, [quotationLens]);

  return (
    <Slide key={index} direction='right' in={true} timeout={index * 300 + 300}>
      <Grid item xs={12} key={index}>
        <QuotationCard
          icon={
            quotationLens.manualQuotation |> get
              ? CreateOutlinedIcon
              : quotationLens.isOfferInNegotiation |> get
              ? UpdateIcon
              : quotationLens.negotiatedOffer |> get
              ? CheckIcon
              : LocalOfferIcon
          }
          iconColor={cardIconColor}
          selected={quotationSelected}
          disabled={
            (quotationLens.sourceQuotation |> get) ||
            (quotationLens.lockedYN |> get) ||
            (!(quotationLens.isActive |> get) && !(quotationLens.manualQuotation |> get))
          }
          isLastModified={isLastQuotationModified}
          title={
            <Tooltip title={showTitleTooltip ? quotationLens.insurerName |> get : ''} placement={'top'}>
              <Typography component={'span'}>
                <Typography color='secondary' variant='subtitle2' fontWeight='bold'>
                  {quotationLens.isOfferInNegotiation |> get
                    ? t('Offer.Edit.Casco.Header.OfferInNegotiation')
                    : quotationLens.negotiatedOffer |> get
                    ? t('Offer.Edit.Casco.Header.OfferNegotiated')
                    : null}
                </Typography>
                <Grid container>
                  <Grid item xs={displayLockArea ? 11 : 12}>
                    <Typography variant='subtitle1' align='center' component={'span'}>
                      <Box fontWeight={'fontWeightBold'}>
                        <Grid container justifyContent={'center'} alignItems={'center'} sx={{ gap: '.5rem' }}>
                          {quotationLens.insurerName |> get}
                          {isLastQuotationModified && (
                            <Tooltip title={t('Offer.Edit.Casco.Header.LastQuotationModified')}>
                              <InfoOutlinedIcon fontSize='inherit' />
                            </Tooltip>
                          )}
                          {(quotationLens.manualQuotation |> get) === true && (
                            <Tooltip title={t('Offer.Edit.Casco.Header.ManualQuotation')}>
                              <NewReleasesIcon />
                            </Tooltip>
                          )}
                        </Grid>
                      </Box>
                    </Typography>
                  </Grid>
                  {displayLockArea && (
                    <Grid item xs={1} container justifyContent='end'>
                      {allowQuotationsLockUpdate ? (
                        <IconButton
                          id={`quotation_lock_${quotationLens.quotationId |> get}`}
                          color='primary'
                          variant='text'
                          disabled={lockUpdating}
                          tooltip={
                            lockUpdating
                              ? ''
                              : quotationLens.lockedYN |> get
                              ? t('Offer.Edit.Casco.Header.UnlockQuotation')
                              : t('Offer.Edit.Casco.Header.LockQuotation')
                          }
                          onClick={() => onQuotationLockChange(!(quotationLens.lockedYN |> get))}
                        >
                          {lockUpdating ? <HourglassEmptyIcon /> : quotationLens.lockedYN |> get ? <LockIcon /> : <LockOpenIcon />}
                        </IconButton>
                      ) : quotationLens.lockedYN |> get ? (
                        <IconButton
                          id={`quotation_lock_${quotationLens.quotationId |> get}`}
                          color='primary'
                          variant='text'
                          tooltip={t('Offer.Edit.Casco.Header.QuotationLocked')}
                          disabled
                        >
                          <LockIcon />
                        </IconButton>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Typography>
            </Tooltip>
          }
        >
          <>
            <Grid item container xs={12}>
              <Grid item container direction='column' xs={12} lg={5}>
                <CardRow
                  label={t('Offer.Edit.Casco.Header.ConventionName')}
                  value={
                    <Tooltip title={showTitleTooltip ? quotationLens.conventionName |> get : ''} placement={'top'}>
                      <ConventionNameWrapper ref={titleRef}>{quotationLens.conventionName |> get}</ConventionNameWrapper>
                    </Tooltip>
                  }
                  flexDisplayedValue={false}
                />
                <CardRow
                  label={t('Offer.Edit.Casco.Header.InsuredAmount', {
                    currency: quotationLens.currencyCode |> get |> valueOrDefault(emptyString)
                  })}
                  value={
                    <TextField
                      id={composeId('InsuredAmount')}
                      fullWidth
                      isNumeric
                      value={quotationLens.insuredAmount |> get |> valueOrDefault(emptyString)}
                      onChange={onHeaderPropertyChange('insuredAmount')}
                      disabled={!allowEditForNegotiation('Comparator.EditQuotation.InsuredAmount')}
                      error={!isValid(validation.insuredAmount)}
                      helperText={getErrors(validation.insuredAmount)}
                    />
                  }
                  flexDisplayedValue={false}
                />
                <CardRow
                  label={t('Offer.Edit.Casco.Header.Periodicity')}
                  value={
                    <TextField
                      id={composeId('Periodicity')}
                      fullWidth
                      isNumeric
                      inputProps={{ decimalScale: 0 }}
                      value={quotationLens.periodicity |> get |> valueOrDefault(emptyString)}
                      onChange={onHeaderPropertyChange('periodicity')}
                      disabled={!allowEditForNegotiation('Comparator.EditQuotation.Periodicity')}
                      error={!isValid(validation.periodicity)}
                      helperText={getErrors(validation.periodicity)}
                    />
                  }
                  flexDisplayedValue={false}
                />
                <CardRow
                  flexDisplayedValue={false}
                  label={t('Offer.Edit.Casco.Header.MonthNo')}
                  value={
                    <TextField
                      value={quotationLens.monthNo |> get |> valueOrDefault(emptyString)}
                      disabled
                      fullWidth
                      isNumeric
                      inputProps={{ decimalScale: 0 }}
                    />
                  }
                />
                <CardRow
                  label={t('Offer.Edit.Casco.Header.InsuranceAmountWithCurrency', {
                    currency: quotationLens.currencyCode |> get |> valueOrDefault(emptyString)
                  })}
                  value={
                    quotationLens.errorMessage |> get ? (
                      <>
                        <Tooltip title={quotationLens.errorMessage |> get |> valueOrDefault(emptyString)}>
                          <IconButton id={composeId('ErrorMessage')} size='small' onClick={() => setShowErrorMessage(!showErrorMessage)}>
                            <ErrorIcon color='error' fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <LoadingWrapper
                        loading={
                          status !== QuotationsStatus.EXPIRED &&
                          !((quotationLens.insuranceAmount |> get) || (quotationLens.insuranceAmount |> get) === 0) &&
                          !(quotationLens.sourceQuotationId |> get)
                        }
                      >
                        {(quotationLens.sourceQuotationId |> get) === null && !(quotationLens.manualQuotation |> get) ? (
                          <TextField
                            value={quotationLens.insuranceAmount |> get |> valueOrDefault(emptyString)}
                            disabled
                            fullWidth
                            isNumeric
                            error={!isValid(validation.insuranceAmount)}
                            helperText={getErrors(validation.insuranceAmount)}
                            endAdornment={
                              <IconButton
                                id={composeId('Retry')}
                                color='primary'
                                size='small'
                                variant='text'
                                tooltip={t('Tooltips.Retry')}
                                onClick={onRetry}
                                disabled={disabled('Comparator.RetryCASCOQuotation') || !allowRetry}
                              >
                                <Replay fontSize='small' />
                              </IconButton>
                            }
                          />
                        ) : (
                          <TextField
                            value={quotationLens.insuranceAmount |> get}
                            onChange={onAmountChange}
                            fullWidth
                            isNumeric
                            error={!isValid(validation.insuranceAmount)}
                            helperText={getErrors(validation.insuranceAmount)}
                            disabled={!allowEditForNegotiation('Comparator.EditQuotation.InsuranceAmount')}
                          />
                        )}
                      </LoadingWrapper>
                    )
                  }
                  flexDisplayedValue={false}
                />
                <CardRow
                  label={t('Offer.Edit.Casco.Header.PeriodMonth')}
                  value={
                    <TextField
                      value={quotationLens.period |> get |> valueOrDefault(emptyString)}
                      disabled
                      fullWidth
                      isNumeric
                      inputProps={{ decimalScale: 0 }}
                    />
                  }
                  flexDisplayedValue={false}
                />
                <CardRow
                  label={t('Offer.Edit.Casco.Header.TotalInsuranceAmountWithCurrency', {
                    currency: quotationLens.currencyCode |> get |> valueOrDefault(emptyString)
                  })}
                  value={
                    quotationLens.errorMessage |> get ? (
                      t('Offer.Edit.Casco.Header.InsuranceAmountNA')
                    ) : (
                      <LoadingWrapper
                        loading={
                          status !== QuotationsStatus.EXPIRED &&
                          !((quotationLens.totalInsuranceAmount |> get) || (quotationLens.totalInsuranceAmount |> get) === 0) &&
                          !(quotationLens.sourceQuotationId |> get)
                        }
                      >
                        <TextField
                          value={quotationLens.totalInsuranceAmount |> get |> valueOrDefault(emptyString)}
                          disabled
                          fullWidth
                          isNumeric
                          error={!isValid(validation.totalInsuranceAmount)}
                          helperText={getErrors(validation.totalInsuranceAmount)}
                        />
                      </LoadingWrapper>
                    )
                  }
                  flexDisplayedValue={false}
                />
                <CardRow
                  flexDisplayedValue={false}
                  label={t('Offer.Edit.Casco.Header.PartialDamageDeductibleAmountWithCurrency', {
                    currency:
                      quotationLens.partialDamageDeductibleCurrency |> get
                        ? quotationLens.partialDamageDeductibleCurrency |> get
                        : quotationLens.currencyCode |> get
                  })}
                  value={
                    <Autocomplete
                      id={composeId('Partial_Damage_Deductible')}
                      fullWidth
                      isSearchable
                      isNumeric
                      simpleValue={true}
                      disabled={disabled('Comparator.EditQuotation.PartialDamageDeductibleAmount')}
                      labelKey={'value'}
                      valueKey={'value'}
                      value={quotationLens.partialDamageDeductibleAmount |> get |> valueOrDefault(emptyString)}
                      onChange={onHeaderPropertyChange('partialDamageDeductibleAmount')}
                      options={quotationLens.partialDamageDeductibleCosts |> get |> valueOrDefault(emptyArray)}
                    />
                  }
                />

                <CardRow
                  flexDisplayedValue={false}
                  label={t('Offer.Edit.Casco.Header.TotalDamageDeductiblePercent')}
                  value={
                    <Autocomplete
                      id={composeId('Total_Damage_Deductible')}
                      fullWidth
                      isSearchable
                      isNumeric
                      simpleValue={true}
                      disabled={disabled('Comparator.EditQuotation.TotalDamageDeductiblePercent')}
                      labelKey={'value'}
                      valueKey={'value'}
                      value={quotationLens.totalDamageDeductiblePercent |> get |> valueOrDefault(emptyString)}
                      onChange={onHeaderPropertyChange('totalDamageDeductiblePercent')}
                      options={quotationLens.totalDamageDeductibleCosts |> get |> valueOrDefault(emptyArray)}
                    />
                  }
                />

                {showErrorMessage && (
                  <Grid container>
                    <Grid item>
                      <Typography variant='caption'>
                        <Box fontStyle='italic'>{quotationLens.errorMessage |> get}</Box>
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                <CardRow
                  label={t('Offer.Edit.Casco.Header.DiscountAmount', {
                    currency: quotationLens.currencyCode |> get |> valueOrDefault(emptyString)
                  })}
                  value={
                    <TextField
                      id={composeId('Discount_Amount')}
                      fullWidth
                      isNumeric
                      disabled={disabled('Comparator.EditQuotation.DiscountAmount')}
                      value={quotationLens.discountAmount |> get |> valueOrDefault(emptyString)}
                      onChange={onHeaderPropertyChange('discountAmount')}
                    />
                  }
                  flexDisplayedValue={false}
                />
                <CardRow
                  label={t('Offer.Edit.Casco.Header.DiscountPercent')}
                  value={
                    <TextField
                      id={composeId('Discount_Percent')}
                      fullWidth
                      isNumeric
                      disabled={disabled('Comparator.EditQuotation.DiscountPercent')}
                      value={quotationLens.discountPercent |> get |> valueOrDefault(emptyString)}
                      onChange={onHeaderPropertyChange('discountPercent')}
                    />
                  }
                  flexDisplayedValue={false}
                />
              </Grid>
              <Hidden only={['xs', 'sm', 'md']}>
                <Grid item container lg={1} justifyContent='center'>
                  <Divider orientation={'vertical'} />
                </Grid>
              </Hidden>

              <Grid item container direction='column' justifyContent='space-between' xs={12} lg={6}>
                <Grid item container>
                  <RisksComponent
                    risks={quotationLens.risks |> sequence}
                    colorTheme={colorTheme}
                    onRiskSelectionChange={onRiskSelectionChange}
                    disabled={disabled('Comparator.EditQuotation.Risks')}
                    cardIconColor={cardIconColor}
                  />
                  <CoefficientsComponent
                    quotation={quotationLens}
                    colorTheme={colorTheme}
                    onCoefficientsSelectionChange={onCoefficientsSelectionChange}
                    disabled={disabled('Comparator.EditQuotation.Coeficients')}
                    allowEmptySelection={allowEmptySelection}
                    validation={validation}
                    cardIconColor={cardIconColor}
                  />
                </Grid>
                <Grid item container spacing={1} justifyContent={'flex-end'}>
                  <Grid item xs={6} sm={5} md={4} lg={3}>
                    {quotationLens.manualQuotation |> get ? (
                      <Button
                        fullWidth
                        id={composeId('Edit_Convention')}
                        onClick={() => onEditCustomQuotation(quotationLens.quotationId |> get)}
                        disabled={!allowCustomQuotationEdit}
                        color='primary'
                      >
                        {t('Tooltips.Edit')}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        id={composeId('Save_Convention')}
                        onClick={() => onSave(quotationLens)}
                        disabled={
                          !isDirty || (disabled('Comparator.EditQuotation') && !allowEditForNegotiation('Comparator.EditQuotation'))
                        }
                        color='primary'
                      >
                        {saving ? t('General.Saving') : t('General.Save')}
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={6} sm={5} md={4} lg={3}>
                    <QuotationButton
                      fullWidth
                      id={composeId('Select_Convention')}
                      onClick={() => onSelect(quotationLens)}
                      disabled={!allowSelection}
                      endIcon={quotationSelected && <CheckCircleIcon />}
                      quotationSelected={quotationSelected}
                    >
                      {quotationSelected ? t('Offer.Edit.Casco.Header.SelectedQuotation') : t('General.Select')}
                    </QuotationButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {(quotationLens.freeTxt |> get) && (
              <Grid item xs={12}>
                <Tooltip title={showMessageTooltip ? quotationLens.freeTxt |> get : ''} placement={'top'}>
                  <Typography variant='caption'>
                    <Box ref={messageRef} fontStyle='italic' mt={1}>
                      {quotationLens.freeTxt |> get}
                    </Box>
                  </Typography>
                </Tooltip>
              </Grid>
            )}
            {!(quotationLens.manualQuotation |> get) && !(quotationLens.isActive |> get) && (
              <Grid item container xs={12} alignContent='flex-start' justifyContent='flex-start' mt={1}>
                <Chip label={t('Offer.Edit.Casco.UnavailableOffer')} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant='caption'>
                <Box mt={1}>
                  <i>
                    {t('Offer.Edit.Casco.LastEdit', {
                      date: t('DATE_FORMAT', {
                        date: { value: quotationLens.quotationDate |> get, format: 'DD.MM.YYYY HH:mm:ss' }
                      })
                    })}
                  </i>
                </Box>
              </Typography>
            </Grid>
          </>
        </QuotationCard>
      </Grid>
    </Slide>
  );
};

QuotationListComponent.propTypes = {
  quotationLens: PropTypes.object,
  validation: PropTypes.object,
  index: PropTypes.number,
  colorTheme: PropTypes.string,
  onSave: PropTypes.func,
  onRetry: PropTypes.func,
  onSelect: PropTypes.func,
  onQuotationLockChange: PropTypes.func,
  isDirty: PropTypes.bool,
  saving: PropTypes.bool,
  lockUpdating: PropTypes.bool,
  disabled: PropTypes.func,
  allowEditForNegotiation: PropTypes.func,
  allowSelection: PropTypes.bool,
  allowCustomQuotationEdit: PropTypes.bool,
  allowQuotationsLockUpdate: PropTypes.bool,
  allowRetry: PropTypes.bool,
  onRiskSelectionChange: PropTypes.func,
  onCoefficientsSelectionChange: PropTypes.func,
  onDeleteCoefficientSelection: PropTypes.func,
  onHeaderPropertyChange: PropTypes.func,
  onAmountChange: PropTypes.func,
  quotationSelected: PropTypes.bool,
  titleRef: PropTypes.func,
  showTitleTooltip: PropTypes.bool,
  messageRef: PropTypes.func,
  showMessageTooltip: PropTypes.bool,
  onEditCustomQuotation: PropTypes.func,
  allowEmptySelection: PropTypes.bool,
  isLastQuotationModified: PropTypes.bool
};

export default QuotationListComponent;
